import React, { useState, useEffect } from "react";
import { ListItem, Typography, Box, Paper, Grid } from "@mui/material";
import YFSLabelContainer from "./YFSLabelContainer";
import YFSLabelVerifications from "./YFSLabelVerifications";
import "./index.css";
import productInfoIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_product_icon.svg";
import productCertIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_cert_icon.svg";
import productScopeIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_scopes_icon.svg";
import productVariationIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_variations_icon.svg";
import YFSLabelItem from "./YFSLabelItem";
import YFSLabelTable from "./YFSLabelTable";
import CustomStatusCheckbox from "./CustomStatusCheckbox";
import CustomStatusCheckboxView from "./CustomStatusCheckboxView";

const initializePublicItems = (data) => {
  const initialState = {};
  Object.keys(data).forEach((key) => {
    if (key === "certificates") {
      initialState[key] = [];
    } else if (key === "variation_categories") {
      initialState[key] = data[key].reduce((acc, cat) => {
        acc[cat.name] = [];
        return acc;
      }, {});
    } else {
      initialState[key] = null;
    }
  });
  return initialState;
};

const ProductSummary = ({
  productData,
  editMode,
  setCompletePublicItems,
  selectedValues,
  categories,
  setCategories,
  yfsRetrieve,
  publicizedItems,
}) => {
  // const [expandedCategories, setExpandedCategories] = useState({});
  const [publicItems, setPublicItems] = useState(
    publicizedItems ? publicizedItems : initializePublicItems(productData)
  );

  useEffect(() => {
    if (setCompletePublicItems) {
      setCompletePublicItems((prevItems) => ({
        ...prevItems,
        ...publicItems,
      }));
    }
  }, [publicItems, categories, setCompletePublicItems]);

  // const handleCategoryToggle = (categoryId) => {
  //   setExpandedCategories((prev) => ({
  //     ...prev,
  //     [categoryId]: !prev[categoryId],
  //   }));
  // };

  const handlePublicToggle = (key, value, subKey = null) => {
    setPublicItems((prev) => {
      const newState = { ...prev };
      if (key === "certificates") {
        // Ensure certificates is always an array
        const currentCerts = Array.isArray(newState[key])
          ? [...newState[key]]
          : [];
        const index = currentCerts.indexOf(value);
        if (index !== -1) {
          currentCerts.splice(index, 1);
        } else {
          currentCerts.push(value);
        }
        newState[key] = currentCerts;
      } else if (key === "variation_categories") {
        newState[key] = newState[key] || {};
        if (subKey) {
          newState[key][subKey] = newState[key][subKey] || [];
          const index = newState[key][subKey].findIndex(
            (v) => v.id === value.id
          );
          if (index !== -1) {
            newState[key][subKey].splice(index, 1);
          } else {
            newState[key][subKey].push(value);
          }
        } else {
          if (
            newState[key][value.name] &&
            newState[key][value.name].length === value.values.length
          ) {
            delete newState[key][value.name];
          } else {
            newState[key][value.name] = value.values.map((v) => ({
              id: v.id,
              value: v.value,
            }));
          }
        }
      } else {
        newState[key] = newState[key] === value ? null : value;
      }
      return newState;
    });
  };

  const handleCategoryChange = (key, category, itemIdentifier = null) => {
    setCategories((prev) => {
      const newCategories = { ...prev };

      if (key === "certificates") {
        // Initialize certificates object if it doesn't exist
        newCategories.certificates = newCategories.certificates || {};
        if (itemIdentifier) {
          // Set the category for the specific certificate
          newCategories.certificates[itemIdentifier] = category;
        }
      } else if (key === "variation_categories") {
        // Initialize variation_categories object if it doesn't exist
        newCategories.variation_categories =
          newCategories.variation_categories || {};
        if (itemIdentifier) {
          // For category values or category names
          if (itemIdentifier.includes("_")) {
            // This is a specific value within a category
            newCategories.variation_categories[itemIdentifier] = category;
          } else {
            // This is a category name
            newCategories.variation_categories[itemIdentifier] = category;
          }
        }
      } else {
        // Handle other fields normally
        newCategories[key] = category;
      }

      return newCategories;
    });
  };

  // In the renderEditControls function:
  const renderEditControls = (key, value, subKey = null) => {
    let checked = false;
    let categoryKey = key;

    if (key === "certificates") {
      checked = publicItems.certificates?.includes(value);
      // Use the certificate name as the identifier
      categoryKey = `${key}_${value}`;
    } else if (key === "variation_categories") {
      checked = subKey
        ? publicItems.variation_categories?.[subKey]?.some(
            (v) => v.id === value.id
          )
        : publicItems[key]?.[value.name]?.length === value.values.length;
      // Use the variation category name or specific value as the identifier
      categoryKey = subKey
        ? `${key}_${subKey}_${value.id}`
        : `${key}_${value.name}`;
    } else {
      checked = publicItems[key] === value;
    }
    if (!editMode && yfsRetrieve)
      return (
        <CustomStatusCheckboxView
          itemKey={categoryKey}
          categories={categories}
          checked={checked}
        />
      );
    else if (!editMode) {
      return;
    }

    return (
      <CustomStatusCheckbox
        itemKey={categoryKey}
        checked={checked}
        onChange={() => handlePublicToggle(key, value, subKey)}
        categories={categories}
        handleCategoryChange={(itemKey, category) =>
          handleCategoryChange(
            key,
            category,
            value?.cert_name || value?.name || value?.id || value
          )
        }
      />
    );
  };

  const renderInfoGroup = () => {
    // Collect all items into a single array
    const allItems = [
      {
        type: "basic",
        name: "Name",
        value: productData.product_name || "N/A",
        controlKey: "product_name",
        controlValue: productData.product_name,
      },
      {
        type: "basic",
        name: "Product Number",
        value: productData.product_num || "N/A",
        controlKey: "product_num",
        controlValue: productData.product_num,
      },
      {
        type: "basic",
        name: "Country of Origin",
        value: productData.country_origin || "N/A",
        controlKey: "country_origin",
        controlValue: productData.country_origin,
      },
      ...(productData.variation_categories || []).map((category) => ({
        type: "category",
        category,
      })),
    ];

    // Calculate the midpoint to split items evenly
    const midPoint = Math.ceil(allItems.length / 2);

    // Split items into two columns
    const firstColumnItems = allItems.slice(0, midPoint);
    const secondColumnItems = allItems.slice(midPoint);

    const renderItem = (item) => {
      if (item.type === "basic") {
        return (
          <Grid item xs={12} key={item.name} display="flex">
            <YFSLabelItem name={item.name} value={item.value} />
            {renderEditControls(item.controlKey, item.controlValue)}
          </Grid>
        );
      } else {
        // Render category item
        const category = item.category;
        return (
          <Grid
            item
            xs={12}
            key={category.id}
            display="flex"
            alignItems="center"
            width="100%"
          >
            <YFSLabelItem
              name={category.name}
              value={category.values.map((v) => v.value).join(", ")}
            />
            {/* <IconButton onClick={() => handleCategoryToggle(category.id)}>
                {expandedCategories[category.id] ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton> */}

            {renderEditControls("variation_categories", category)}
            {/* <Collapse in={expandedCategories[category.id]}>
              <List>
                {category.values.map((value) => (
                  <ListItem key={value.id}>
                    <Typography sx={{ fontWeight: "light", color: "#203669" }}>
                      {value.value}
                    </Typography>
                    {renderEditControls(
                      "variation_categories",
                      value,
                      category.name
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse> */}
          </Grid>
        );
      }
    };

    return (
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2 }}
        className="product-info-container"
      >
        <img
          src={productVariationIcon}
          alt="Product Variation Icon"
          width={30}
          height={30}
          style={{ position: "absolute", top: "-10px", left: "3px" }}
        />
        <Grid container spacing={2}>
          {/* First Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {firstColumnItems.map((item) => renderItem(item))}
            </Grid>
          </Grid>

          {/* Second Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {secondColumnItems.map((item) => renderItem(item))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderEnvironmentalImpactGroup = () => (
    <Paper elevation={0} sx={{ p: 2, mb: 2 }} className="product-env-container">
      <img
        src={productInfoIcon}
        alt="Product Info Icon"
        width={30}
        height={30}
        style={{ position: "absolute", top: "-10px", left: "-2px" }}
      />
      <Grid container spacing={2}>
        {[
          { key: "prod_weight", label: "Product weight" },
          { key: "total_virgin", label: "Virgin materials" },
          { key: "total_recycle", label: "Recycled materials" },
          { key: "prod_water_total", label: "Water used" },
          { key: "prod_waste", label: "Waste" },
          { key: "total_packaging", label: "Packaging" },
          { key: "total_chemicals", label: "Chemicals used" },
        ].map(({ key, label }) => (
          <Grid item xs={12} key={key}>
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              sx={{
                "& > *:first-of-type": {
                  flexGrow: 1,
                },
              }}
            >
              <YFSLabelItem
                name={label}
                value={productData[key] || "N/A"}
                unit={
                  key.includes("weight") ||
                  key.includes("materials") ||
                  key.includes("prod_waste") ||
                  key === "total_packaging"
                    ? "kg"
                    : ""
                }
              />
              {renderEditControls(key, productData[key])}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderProductScopes = () => (
    <Paper
      elevation={0}
      sx={{ p: 2, mb: 2 }}
      className="product-scope-container"
    >
      <img
        src={productScopeIcon}
        alt="Product Scope Icon"
        width={30}
        height={30}
        style={{ position: "absolute", top: "-10px", left: "-2px" }}
      />
      <Grid container spacing={2}>
        {[{ key: "total_prod_co2", label: "tCO2e" }].map(({ key, label }) => (
          <Grid item xs={12} key={key}>
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              sx={{
                "& > *:first-of-type": {
                  flexGrow: 1,
                },
              }}
            >
              <YFSLabelItem
                name={label}
                value={productData[key]?.toFixed(2) || "N/A"}
              />
              {renderEditControls(key, productData[key])}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderCertificationsGroup = () => (
    <Paper
      elevation={0}
      sx={{ p: 2, mb: 2, maxHeight: "max-content" }}
      className="product-cert-container"
    >
      <img
        src={productCertIcon}
        alt="Product Certificate Icon"
        width={30}
        height={30}
        style={{ position: "absolute", top: "-10px", left: "3px" }}
      />
      <YFSLabelTable
        label={"Certifications"}
        items={
          productData?.certificates &&
          productData?.certificates.map((cert) => (
            <ListItem
              key={cert.id}
              sx={{
                left: 0,
                paddingTop: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "light",
                  color: "#203669",
                  fontSize: "14px",
                }}
              >
                - {cert.cert_name}
              </Typography>
              {renderEditControls("certificates", cert.cert_name)}
            </ListItem>
          ))
        }
      />
      <Grid item xs={12} mt={3}>
        <YFSLabelTable
          label={"Repairs"}
          items={
            <Typography
              sx={{
                fontWeight: "light",
                color: "#203669",
                textAlign: "center",
                fontSize: "14px",
              }}
            >
              {productData.repairs || "N/A"}
            </Typography>
          }
        />
        {renderEditControls("repairs", productData.repairs)}
      </Grid>
      <Grid item xs={12} mt={3}>
        <YFSLabelTable
          label={"Professional Disassembly"}
          items={
            <Typography
              sx={{
                fontWeight: "light",
                color: "#203669",
                textAlign: "center",
                fontSize: "14px",
              }}
            >
              {productData.professional_disassembly || "N/A"}
            </Typography>
          }
        />
        {renderEditControls(
          "professional_disassembly",
          productData.professional_disassembly
        )}
      </Grid>
      <Grid item xs={12} mt={3}>
        <YFSLabelTable
          label={"User Recycling"}
          items={
            productData.user_recycling &&
            productData.user_recycling.map((item) => (
              <ListItem
                sx={{
                  left: 0,
                  paddingTop: 0,
                }}
                key={item}
              >
                {item}
              </ListItem>
            ))
          }
        />
        {renderEditControls("user_recycling", productData.user_recycling)}
      </Grid>
    </Paper>
  );

  return (
    <YFSLabelContainer>
      <YFSLabelVerifications editMode={editMode} />
      {renderInfoGroup()}
      <div style={{ display: "flex", marginBottom: "80px" }}>
        <div style={{ width: "50%" }}>{renderCertificationsGroup()}</div>

        <div style={{ marginLeft: "22px", width: "50%" }}>
          {renderEnvironmentalImpactGroup()}
          {renderProductScopes()}
        </div>
      </div>
    </YFSLabelContainer>
  );
};

export default ProductSummary;
